import {
  ChatAppliedRouteStop,
  ChatRouteStopsResult,
  ChatSkippedRouteStop,
  ChatSkippedRouteStopReason,
} from "../../types/chat/ChatContent";

import { isRecord, ROUTE_STOP_ENTITY_TYPES } from "./chatWireGuards";

const REASONS: ChatSkippedRouteStopReason[] = ["alreadyOnRoute", "noLocation", "notAccessible"];

const parseAppliedRouteStop = (value: unknown): ChatAppliedRouteStop | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const entityType = ROUTE_STOP_ENTITY_TYPES.find((candidate) => candidate === value.entityType);
  return entityType && typeof value.displayName === "string" && typeof value.entityId === "number"
    ? { displayName: value.displayName, entityId: value.entityId, entityType }
    : undefined;
};

const parseSkippedRouteStop = (value: unknown): ChatSkippedRouteStop | undefined => {
  const stop = parseAppliedRouteStop(value);
  return stop && isRecord(value)
    ? { ...stop, reason: REASONS.find((reason) => reason === value.reason) ?? null }
    : undefined;
};

const parseList = <T>(value: unknown[], parse: (entry: unknown) => T | undefined): T[] =>
  value.map(parse).filter((entry): entry is T => !!entry);

// The records an add-to-route request named that the platform left off, on the action of both a
// picker and the single-stop add it can fall through to. A spread, so an empty or absent list adds
// no key at all. An entry with no record to name is dropped, since there is nothing to show for
// it, but never silently.
const parseSkippedRouteStops = (
  value: unknown,
  warnings: string[]
): { skippedStops?: ChatSkippedRouteStop[] } => {
  if (!Array.isArray(value)) {
    return {};
  }
  const skippedStops = parseList(value, parseSkippedRouteStop);
  if (skippedStops.length < value.length) {
    warnings.push('Dropped malformed "manage_route_stops" skippedStops entries');
  }
  return skippedStops.length ? { skippedStops } : {};
};

// An apply result's `routeStops`. It is stored with the chat and rendered after every reload, so it
// is checked here rather than trusted: a result without both lists is not one the card can show.
// What it drops is reported through `warnings`, since a dropped skipped entry is a record the card
// would otherwise leave off "Not added" without a trace.
export const parseRouteStopsResult = (
  value: unknown,
  warnings: string[]
): ChatRouteStopsResult | undefined => {
  if (
    !isRecord(value) ||
    !Array.isArray(value.appliedStops) ||
    !Array.isArray(value.skippedStops)
  ) {
    warnings.push('Dropped a malformed "routeStops" result');
    return undefined;
  }
  const appliedStops = parseList(value.appliedStops, parseAppliedRouteStop);
  if (appliedStops.length < value.appliedStops.length) {
    warnings.push('Dropped malformed "routeStops" appliedStops entries');
  }
  const skippedStops = parseList(value.skippedStops, parseSkippedRouteStop);
  if (skippedStops.length < value.skippedStops.length) {
    warnings.push('Dropped malformed "routeStops" skippedStops entries');
  }
  return { appliedStops, skippedStops };
};

export default parseSkippedRouteStops;
